import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import Seo from '../components/Seo';
import Image from '../components/common/Image';
import { PageLoader } from '../components/common/Spinner';
import { useSettings } from '../context/SettingsContext';
import { money, formatDate } from '../utils/format';
import { IconCheck } from '../components/common/Icons';
import { payForOrder } from '../utils/razorpay';
import { useUI } from '../context/UIContext';

export const STATUS_LABEL = { pending: 'Pending', confirmed: 'Confirmed', processing: 'Processing', shipped: 'Shipped', delivered: 'Delivered', cancelled: 'Cancelled', returned: 'Returned' };

export function OrderView({ order }) {
  const { settings } = useSettings();
  const symbol = settings.shop?.currencySymbol || '$';
  const a = order.shippingAddress || {};
  return (
    <div className="flex flex-wrap -mx-4">
      <div className="w-full lg:w-7/12 px-4">
        <div className="eg-card mb-6">
          <div className="flex flex-wrap justify-between gap-2 mb-4">
            <div><div className="text-sm text-color-secondary">Order</div><div className="text-xl font-medium">#{order.orderNumber}</div></div>
            <div><div className="text-sm text-color-secondary">Placed</div><div>{formatDate(order.createdAt)}</div></div>
            <div><div className="text-sm text-color-secondary">Status</div><span className={`eg-status eg-status--${order.status}`}>{STATUS_LABEL[order.status] || order.status}</span></div>
            <div><div className="text-sm text-color-secondary">Payment</div><div className="capitalize">{order.paymentMethod} · {order.paymentStatus}</div></div>
          </div>
          {order.trackingNumber && <p className="text-sm">Tracking: {order.trackingUrl ? <a href={order.trackingUrl} className="underline" target="_blank" rel="noreferrer">{order.trackingNumber}</a> : order.trackingNumber}</p>}
          {order.items.map((it, i) => (
            <div key={i} className="flex items-center py-3 border-t">
              <div className="w-16 flex-shrink-0"><Image src={it.image} alt={it.title} ratio={1} /></div>
              <div className="flex-1 px-3"><Link to={`/products/${it.slug}`} className="font-medium">{it.title}</Link>{it.variantTitle && <div className="text-sm text-color-secondary">{it.variantTitle}</div>}<div className="text-sm text-color-secondary">Qty {it.quantity}</div></div>
              <div className="whitespace-nowrap">{money(it.price * it.quantity, symbol)}</div>
            </div>
          ))}
        </div>
        {order.history?.length > 0 && (
          <div className="eg-card">
            <h3 className="font-medium mb-3">Order timeline</h3>
            <ul className="eg-timeline">{order.history.map((h, i) => <li key={i}><span className="capitalize font-medium">{STATUS_LABEL[h.status] || h.status}</span> <span className="text-sm text-color-secondary">– {formatDate(h.at, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>{h.note && <div className="text-sm text-color-secondary">{h.note}</div>}</li>)}</ul>
          </div>
        )}
      </div>
      <div className="w-full lg:w-5/12 px-4 mt-6 lg:mt-0">
        <div className="eg-summary mb-6">
          <h3 className="text-lg font-medium mb-3">Summary</h3>
          <div className="flex justify-between py-1"><span>Subtotal</span><span>{money(order.subtotal, symbol)}</span></div>
          <div className="flex justify-between py-1"><span>Shipping</span><span>{order.shippingCost ? money(order.shippingCost, symbol) : 'Free'}</span></div>
          {order.discount > 0 && <div className="flex justify-between py-1"><span>Discount</span><span>-{money(order.discount, symbol)}</span></div>}
          <div className="flex justify-between py-2 text-lg font-medium border-t mt-2"><span>Total</span><span>{money(order.total, symbol)}</span></div>
        </div>
        <div className="eg-card">
          <h3 className="text-lg font-medium mb-3">Shipping address</h3>
          <p>{a.fullName}<br />{a.line1}{a.line2 ? <><br />{a.line2}</> : null}<br />{[a.city, a.state, a.postalCode].filter(Boolean).join(', ')}<br />{a.country}<br />{a.phone}{a.email ? <><br />{a.email}</> : null}</p>
          {order.note && <p className="text-sm text-color-secondary mt-3">Note: {order.note}</p>}
        </div>
      </div>
    </div>
  );
}

export default function Order() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { settings } = useSettings();
  const { toast } = useUI();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  const [paying, setPaying] = useState(false);
  const symbol = settings.shop?.currencySymbol || '\u20b9';
  const rzEnabled = !!settings.payments?.razorpay?.enabled && !!settings.payments?.razorpay?.keyId;

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.order)).catch((e) => setError(e.message));
  }, [id]);

  // Lets a customer complete payment if they closed the window or it failed.
  const retryPayment = async () => {
    setPaying(true);
    const { paid, order: updated } = await payForOrder({ order, settings, onToast: toast });
    if (paid && updated) {
      setOrder(updated);
      toast('Payment successful');
    }
    setPaying(false);
  };
  if (error) return <div className="container-fluid section-my text-center"><p className="text-color-secondary">{error}</p><Link to="/account" className="sf__btn sf__btn-primary mt-6 inline-block">Go to my account</Link></div>;
  if (!order) return <PageLoader />;
  return (
    <>
      <Seo title={`Order #${order.orderNumber}`} noIndex />
      <div className="container-fluid section-my">
        {params.get('placed') && (
          order.paymentStatus === 'paid' ? (
            <div className="text-center mb-10">
              <div className="eg-success-icon"><IconCheck width={28} height={28} /></div>
              <h1 className="text-3xl md:text-4xl mb-2">Payment successful</h1>
              <p className="text-color-secondary">
                We received <strong>{money(order.total, symbol)}</strong> for order <strong>#{order.orderNumber}</strong>.
                A confirmation is on its way to {order.shippingAddress?.phone}.
              </p>
              {order.paymentRef && <p className="text-sm text-color-secondary mt-2">Payment reference: {order.paymentRef}</p>}
            </div>
          ) : order.paymentMethod === 'razorpay' ? (
            <div className="text-center mb-10">
              <div className="eg-pending-icon">!</div>
              <h1 className="text-3xl md:text-4xl mb-2">Order placed — payment pending</h1>
              <p className="text-color-secondary">
                Order <strong>#{order.orderNumber}</strong> is saved, but we have not received payment yet.
                Nothing has been charged.
              </p>
              {rzEnabled && (
                <button type="button" className="sf__btn sf__btn-primary mt-5" onClick={retryPayment} disabled={paying}>
                  {paying ? 'Opening payment…' : `Pay ${money(order.total, symbol)} now`}
                </button>
              )}
            </div>
          ) : (
            <div className="text-center mb-10">
              <div className="eg-success-icon"><IconCheck width={28} height={28} /></div>
              <h1 className="text-3xl md:text-4xl mb-2">Thank you for your order!</h1>
              <p className="text-color-secondary">Order <strong>#{order.orderNumber}</strong> has been placed. We will contact you on WhatsApp {order.shippingAddress?.phone} to confirm.</p>
            </div>
          )
        )}
        <OrderView order={order} />
        <div className="text-center mt-10"><Link to="/collections/all" className="sf__btn sf__btn-secondary">Continue shopping</Link></div>
      </div>
    </>
  );
}
